import React, { useState, useEffect } from 'react';
import {
  X,
  RefreshCw,
  AlertCircle,
  TrendingUp,
  FileText,
  BookOpen,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { seoApi } from '../api/client';
import type { SiteSEOOverview } from '../schemas/validation';
import { useNavigate } from 'react-router-dom';
import { useRoarySEOAudit } from '../hooks/useRoarySEOAudit';
import { RoarySEOAuditPanel } from './Roary/RoarySEOAuditPanel';
import { useMarshmallows } from '../hooks/useMarshmallows';
import roaryMascot from '/assets/Roary-mascot.png';

interface SiteSEOOverviewPanelProps {
  siteId: string;
  isOpen: boolean;
  onClose: () => void;
  /** When false, hides Roary SEO Audit entry points. */
  showRoaryAudit?: boolean;
}

export const SiteSEOOverviewPanel: React.FC<SiteSEOOverviewPanelProps> = ({
  siteId,
  isOpen,
  onClose,
  showRoaryAudit = true,
}) => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<SiteSEOOverview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAuditPanel, setShowAuditPanel] = useState(false);

  const audit = useRoarySEOAudit();
  const marshmallows = useMarshmallows();

  useEffect(() => {
    if (!isOpen || !siteId) return;

    const fetchOverview = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await seoApi.getSiteOverview(siteId);
        setOverview(result);
      } catch (err: any) {
        setError(err.message || 'Failed to load SEO overview');
        console.error('SEO overview error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOverview();
  }, [isOpen, siteId]);

  const handleRefresh = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await seoApi.getSiteOverview(siteId);
      setOverview(result);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh SEO overview');
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const handlePageClick = (pageId: string) => {
    navigate(`/sites/${siteId}/pages/${pageId}/editor`);
    onClose();
  };

  const handleViewPostInEditor = (postId: string) => {
    // Navigate to blog post editor (blogId should be available but navigate with siteId)
    navigate(`/sites/${siteId}/blog/posts/${postId}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full max-w-3xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">Site SEO Overview</h2>
            {overview && (
              <div
                className={`px-3 py-1 rounded-full ${getScoreBgColor(
                  overview.overview.averageScore
                )}`}
              >
                <span
                  className={`text-sm font-bold ${getScoreColor(overview.overview.averageScore)}`}
                >
                  {overview.overview.averageScore}/100
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {showRoaryAudit && (
              <button
                onClick={() => setShowAuditPanel(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors text-sm font-medium"
                title="Let Roary inspect and fix SEO issues"
              >
                <img src={roaryMascot} alt="" className="w-4 h-4 object-contain" />
                <Sparkles className="w-3.5 h-3.5" />
                Roary Audit
              </button>
            )}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh analysis"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && !overview && (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {overview && (
            <div className="space-y-6">
              {/* Site Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{overview.siteName}</h3>
              </div>

              {/* Overview Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  label="Total Content"
                  value={overview.overview.totalPages + (overview.overview.totalBlogPosts || 0)}
                  icon={<FileText className="w-5 h-5" />}
                />
                <MetricCard
                  label="Average Score"
                  value={`${overview.overview.averageScore}/100`}
                  icon={<TrendingUp className="w-5 h-5" />}
                  valueColor={getScoreColor(overview.overview.averageScore)}
                />
                <MetricCard
                  label="Total Issues"
                  value={overview.overview.totalIssues}
                  icon={<AlertCircle className="w-5 h-5" />}
                  valueColor={overview.overview.totalIssues > 0 ? 'text-red-600' : 'text-green-600'}
                />
                <MetricCard
                  label="Critical Issues"
                  value={overview.overview.criticalIssues}
                  icon={<AlertCircle className="w-5 h-5" />}
                  valueColor={
                    overview.overview.criticalIssues > 0 ? 'text-red-600' : 'text-green-600'
                  }
                />
              </div>

              {/* Breakdown Metrics */}
              {(overview.overview.totalPages > 0 ||
                (overview.overview.totalBlogPosts || 0) > 0) && (
                <div className="grid grid-cols-2 gap-4">
                  <MetricCard
                    label="Pages"
                    value={overview.overview.totalPages}
                    icon={<FileText className="w-5 h-5" />}
                  />
                  {overview.overview.totalBlogPosts !== undefined &&
                    overview.overview.totalBlogPosts > 0 && (
                      <MetricCard
                        label="Blog Posts"
                        value={overview.overview.totalBlogPosts}
                        icon={<BookOpen className="w-5 h-5" />}
                      />
                    )}
                </div>
              )}

              {/* Pages List */}
              {overview.pages && overview.pages.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Pages ({overview.pages.length})
                  </h3>
                  <div className="space-y-2">
                    {overview.pages.map(page => (
                      <div
                        key={page.pageId}
                        onClick={() => handlePageClick(page.pageId)}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <p className="text-sm font-medium text-gray-900 truncate">{page.url}</p>
                          </div>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600">
                            <span>
                              {page.issueCount} issue
                              {page.issueCount !== 1 ? 's' : ''}
                            </span>
                            {page.criticalIssues > 0 && (
                              <span className="text-red-600 font-medium">
                                {page.criticalIssues} critical
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div
                            className={`px-2 py-1 rounded text-xs font-semibold ${getScoreBgColor(
                              page.score
                            )} ${getScoreColor(page.score)}`}
                          >
                            {page.score}/100
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Blog Posts List */}
              {overview.blogPosts && overview.blogPosts.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Blog Posts ({overview.blogPosts.length})
                  </h3>
                  <div className="space-y-2">
                    {overview.blogPosts.map(post => {
                      // Handle both postId and blogPostId field names
                      const postId = (post as any).postId || (post as any).blogPostId;

                      return (
                        <div
                          key={postId}
                          onClick={() => handleViewPostInEditor(postId)}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <BookOpen className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {post.url}
                              </p>
                            </div>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600">
                              <span>
                                {post.issueCount} issue
                                {post.issueCount !== 1 ? 's' : ''}
                              </span>
                              {post.criticalIssues > 0 && (
                                <span className="text-red-600 font-medium">
                                  {post.criticalIssues} critical
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div
                              className={`px-2 py-1 rounded text-xs font-semibold ${getScoreBgColor(
                                post.score
                              )} ${getScoreColor(post.score)}`}
                            >
                              {post.score}/100
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Roary SEO Audit Panel */}
      {showRoaryAudit && showAuditPanel && (
        <RoarySEOAuditPanel
          status={audit.status}
          auditReport={audit.auditReport}
          fixResult={audit.fixResult}
          fixProgress={audit.fixProgress}
          error={audit.error}
          analyzedPages={audit.analyzedPages}
          onStartAudit={() => audit.startAudit(siteId)}
          onStartFix={auditId => audit.startFix(auditId)}
          onCancel={audit.cancel}
          onReset={audit.reset}
          onClose={() => {
            setShowAuditPanel(false);
            audit.reset();
          }}
          isDepleted={marshmallows.isDepleted}
        />
      )}
    </div>
  );
};

interface MetricCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  valueColor?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  icon,
  valueColor = 'text-gray-900',
}) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center space-x-2 text-gray-600 mb-2">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${valueColor}`}>{value}</p>
    </div>
  );
};
